import { computed, Ref } from '@vue/composition-api';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { useGroupMembersStore } from '@front-app/store/singletons/GroupMembers';
import { UserRole } from '@front-app/services/UserApi/UserModel';

const MEMBER_ROLES = [UserRole.MEMBER, UserRole.MODERATOR, UserRole.ADMINISTRATOR, UserRole.INVITEE];

export interface UseMemberConditions {
	hasReachedMaxForRole: Ref<boolean>;
	hasReachedMaxMembers: Ref<boolean>;
	currentNumMembers: Ref<number>;
	currentNumMembersForRole: Ref<number | undefined>;
}

export const useMemberConditions = (groupId: Ref<string>, selectedRole: Ref<UserRole | null>): UseMemberConditions => {
	const group = computed(() => GroupsStore.getInstance().groupById(groupId.value));
	const { getNumMembersByRole } = useGroupMembersStore(computed(() => groupId.value));

	const conditionMaxForRole = computed(() => {
		switch (selectedRole.value) {
		case UserRole.MEMBER:
			return group.value?.conditions.maxMembers;
		case UserRole.MODERATOR:
			return group.value?.conditions.maxModerators;
		case UserRole.ADMINISTRATOR:
			return group.value?.conditions.maxAdministrators;
		default:
			return undefined;
		}
	});
	const currentNumMembers = computed(() => getNumMembersByRole(MEMBER_ROLES));
	const hasReachedMaxMembers = computed(() => currentNumMembers.value >= (group.value?.conditions.maxMembers || 0));
	const currentNumMembersForRole = computed(() => {
		switch (selectedRole.value) {
		case UserRole.MEMBER:
			return currentNumMembers.value;
		case UserRole.MODERATOR:
			return getNumMembersByRole([UserRole.MODERATOR]);
		case UserRole.ADMINISTRATOR:
			return getNumMembersByRole([UserRole.ADMINISTRATOR]);
		default:
			return undefined;
		}
	});
	const hasReachedMaxForRole = computed(() => {
		if (!group.value || conditionMaxForRole.value === undefined || currentNumMembersForRole.value === undefined) {
			return false;
		}
		if (hasReachedMaxMembers.value) {
			return true;
		}
		if (conditionMaxForRole.value === 0) {
			return false;
		}
		return conditionMaxForRole.value <= currentNumMembersForRole.value;
	});

	return {
		hasReachedMaxForRole,
		hasReachedMaxMembers,
		currentNumMembers,
		currentNumMembersForRole,
	};
};
